import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { BarChart3, AlertCircle } from 'lucide-react';

export default function Survey() {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [surveyType, setSurveyType] = useState('job_market');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [responses, setResponses] = useState({});

  const surveys = {
    job_market: {
      title: 'Job Market Survey',
      description: 'Help us understand the job market better',
      questions: [
        { id: 'experience', text: 'Years of experience:', type: 'select', options: ['0-1', '1-3', '3-5', '5-10', '10+'] },
        { id: 'salary_expectation', text: 'Salary expectation:', type: 'select', options: ['0-20k', '20-40k', '40-60k', '60-100k', '100k+'] },
        { id: 'preferred_role', text: 'Preferred job role:', type: 'text' },
        { id: 'skills', text: 'Key skills you possess:', type: 'textarea' },
        { id: 'job_search_status', text: 'Current job search status:', type: 'select', options: ['Actively looking', 'Open to offers', 'Not looking'] }
      ]
    },
    employer_feedback: {
      title: 'Employer Feedback Survey',
      description: 'Help us improve our recruitment platform',
      questions: [
        { id: 'platform_rating', text: 'How would you rate our platform?', type: 'select', options: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] },
        { id: 'candidate_quality', text: 'Quality of candidates received:', type: 'select', options: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] },
        { id: 'hiring_success', text: 'Have you successfully hired through Talent Link?', type: 'select', options: ['Yes', 'No', 'In progress'] },
        { id: 'feedback', text: 'What could we improve?', type: 'textarea' },
        { id: 'recommendation', text: 'Would you recommend Talent Link?', type: 'select', options: ['Yes', 'No', 'Maybe'] }
      ]
    },
    market_research: {
      title: 'Market Research Survey',
      description: 'Help shape the future of recruitment in Zimbabwe',
      questions: [
        { id: 'industry', text: 'Which industry are you in?', type: 'text' },
        { id: 'main_challenge', text: 'Biggest hiring challenge:', type: 'textarea' },
        { id: 'skill_gap', text: 'Skill gaps in your industry:', type: 'textarea' },
        { id: 'training_interest', text: 'Interested in training programs?', type: 'select', options: ['Yes', 'No', 'Maybe'] },
        { id: 'other_feedback', text: 'Any other feedback:', type: 'textarea' }
      ]
    }
  };

  const currentSurvey = surveys[surveyType];

  const handleInputChange = (questionId, value) => {
    setResponses({
      ...responses,
      [questionId]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/api/surveys', {
        survey_type: surveyType,
        responses: responses
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmitted(true);
      setResponses({});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit survey');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-12 max-w-md text-center">
          <BarChart3 className="mx-auto text-green-600 mb-4" size={48} />
          <h1 className="text-2xl font-bold mb-4">Thank You!</h1>
          <p className="text-gray-600 mb-6">Your survey response has been submitted. Your feedback helps us improve Talent Link.</p>
          <button
            onClick={() => {
              setSubmitted(false);
              setSurveyType('job_market');
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Submit Another Survey
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-8 text-center">Talent Link Surveys</h1>

        {/* Survey Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {Object.entries(surveys).map(([key, survey]) => (
            <button
              key={key}
              onClick={() => {
                setSurveyType(key);
                setResponses({});
              }}
              className={`p-4 rounded-lg font-semibold transition text-left ${
                surveyType === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <p className="font-bold">{survey.title}</p>
              <p className="text-sm opacity-75">{survey.description}</p>
            </button>
          ))}
        </div>

        {/* Survey Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-2">{currentSurvey.title}</h2>
          <p className="text-gray-600 mb-6">{currentSurvey.description}</p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
              <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {currentSurvey.questions.map((question) => (
              <div key={question.id}>
                <label className="block text-sm font-medium text-gray-700 mb-2">{question.text}</label>
                {question.type === 'select' ? (
                  <select
                    value={responses[question.id] || ''}
                    onChange={(e) => handleInputChange(question.id, e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    <option value="">Select an option...</option>
                    {question.options.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : question.type === 'textarea' ? (
                  <textarea
                    value={responses[question.id] || ''}
                    onChange={(e) => handleInputChange(question.id, e.target.value)}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                  />
                ) : (
                  <input
                    type="text"
                    value={responses[question.id] || ''}
                    onChange={(e) => handleInputChange(question.id, e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                )}
              </div>
            ))}

            <button
              type="submit"
              disabled={loading || !user}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {!user ? 'Please Login to Submit' : loading ? 'Submitting...' : 'Submit Survey'}
            </button>
          </form>

          {!user && (
            <p className="mt-4 text-center text-gray-600">
              <button
                onClick={() => navigate('/login')}
                className="text-blue-600 hover:underline font-semibold"
              >
                Login
              </button>
              {' '}to submit surveys
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
